// Configuration for columns that support synchronized sorting across all tier tables

export interface SortableColumnConfig {
  key: string;
  label: string;
  dataIndex: string;
  availableIn: readonly ('weekly' | 'quarterly')[];
}

export const SORTABLE_COLUMNS: Record<string, SortableColumnConfig> = {
  // Weekly view columns
  priorWeekTotalSales: {
    key: 'priorWeekTotalSales',
    label: 'Prior Week Total Sales',
    dataIndex: 'eightWeekTotalSales',
    availableIn: ['weekly'] as const,
  },
  priorWeekGfs: {
    key: 'priorWeekGfs',
    label: 'Prior Week GFS Spend',
    dataIndex: 'eightWeekTotalGfs',
    availableIn: ['weekly'] as const,
  },
  avgWeeklySales: {
    key: 'avgWeeklySales',
    label: '8-Wk Avg Weekly Sales',
    dataIndex: 'weeklyTotal',
    availableIn: ['weekly'] as const,
  },
  avgWeeklyGfs: {
    key: 'avgWeeklyGfs',
    label: '8-Wk Avg GFS Spend',
    dataIndex: 'weeklyGfs',
    availableIn: ['weekly'] as const,
  },

  // Quarterly view columns
  quarterWeeklySales: {
    key: 'quarterWeeklySales',
    label: 'Qtr Weekly Avg Total Sales',
    dataIndex: 'weeklyTotal',
    availableIn: ['quarterly'] as const,
  },
  quarterWeeklyGfs: {
    key: 'quarterWeeklyGfs',
    label: 'Qtr Weekly Avg GFS Spend',
    dataIndex: 'weeklyGfs',
    availableIn: ['quarterly'] as const,
  },
  quarterTotalSales: {
    key: 'quarterTotalSales',
    label: 'Total Sales',
    dataIndex: 'eightWeekTotalSales',
    availableIn: ['quarterly'] as const,
  },
  quarterTotalGfs: {
    key: 'quarterTotalGfs',
    label: 'Total GFS',
    dataIndex: 'eightWeekTotalGfs',
    availableIn: ['quarterly'] as const,
  },

  // Shared columns (available in both views)
  gfsPercent: {
    key: 'gfsPercent',
    label: 'GFS %',
    dataIndex: 'gfsPercent',
    availableIn: ['weekly', 'quarterly'] as const,
  },
  varianceFromAvg: {
    key: 'varianceFromAvg',
    label: 'Variance from Avg',
    dataIndex: 'varianceFromAvg',
    availableIn: ['weekly', 'quarterly'] as const,
  },
  location: {
    key: 'location',
    label: 'Location',
    dataIndex: 'location',
    availableIn: ['weekly', 'quarterly'] as const,
  },
  group: {
    key: 'group',
    label: 'DM',
    dataIndex: 'group',
    availableIn: ['weekly', 'quarterly'] as const,
  },
};

export type SortableColumnKey = keyof typeof SORTABLE_COLUMNS;

export interface GlobalSortState {
  columnKey: string | null;
  order: 'ascend' | 'descend' | null;
}

// Helper to get sortable columns for a specific view mode
export const getSortableColumnsForView = (viewMode: 'weekly' | 'quarterly'): SortableColumnConfig[] => {
  return Object.values(SORTABLE_COLUMNS).filter(col => col.availableIn.includes(viewMode));
};

// Helper to get the display label for a sort column
export const getSortColumnLabel = (columnKey: string | null): string => {
  if (!columnKey) return '';
  return SORTABLE_COLUMNS[columnKey]?.label || columnKey;
};
